import { createContext, useContext, useEffect, useState, type RefObject } from "react";

import { noteFrame, type Note } from "./api";
import { MicIcon } from "./shell";

// RV-6 — highlight to comment, in every room. Select words in an item (in a post, an email, a
// SIR, a question, or inside a drawn page) and a Comment button appears; the note (typed or a
// voice note) is pinned to the words you selected. Each item lists its notes with their passage.
// A drawn page lives in an iframe, so it reports its own selections with an "lr-select" event.

export type Picked = { quote: string; itemId: string; x: number; y: number; touch: boolean; anchor?: string };

export function useTextPick(root: RefObject<HTMLElement>): [Picked | null, () => void] {
  const [picked, setPicked] = useState<Picked | null>(null);
  useEffect(() => {
    const fromSelection = (e: Event) => {
      const s = window.getSelection();
      const quote = (s?.toString() || "").replace(/\s+/g, " ").trim();
      if (!s || quote.length < 3 || !s.rangeCount || !root.current) return;
      const node = s.anchorNode && (s.anchorNode.nodeType === 1 ? (s.anchorNode as Element) : s.anchorNode.parentElement);
      if (!node || !root.current.contains(node)) return;
      const item = node.closest<HTMLElement>("[data-item]");
      if (!item?.dataset.item) return;
      const r = s.getRangeAt(0).getBoundingClientRect();
      setPicked({ quote, itemId: item.dataset.item, x: r.left + r.width / 2, y: r.top, touch: e.type === "touchend" });
    };
    const fromFrame = (e: Event) => {
      const d = (e as CustomEvent).detail as { quote: string; x: number; y: number; frame: HTMLElement };
      const item = d.frame.closest<HTMLElement>("[data-item]");
      if (item?.dataset.item && d.quote.length >= 3) setPicked({ quote: d.quote, itemId: item.dataset.item, x: d.x, y: d.y, touch: false });
    };
    const later = (e: Event) => window.setTimeout(() => fromSelection(e), 10);
    document.addEventListener("mouseup", later);
    document.addEventListener("touchend", later);
    window.addEventListener("lr-select", fromFrame);
    return () => { document.removeEventListener("mouseup", later); document.removeEventListener("touchend", later); window.removeEventListener("lr-select", fromFrame); };
  }, [root]);
  return [picked, () => setPicked(null)];
}

// a drawn page (same-origin iframe, no scripts) reports text selected inside it
export function watchFrameSelection(frame: HTMLIFrameElement) {
  const doc = frame.contentDocument;
  if (!doc) return () => {};
  const up = () => window.setTimeout(() => {
    const s = doc.getSelection();
    const quote = (s?.toString() || "").replace(/\s+/g, " ").trim();
    if (!s || quote.length < 3 || !s.rangeCount) return;
    const r = s.getRangeAt(0).getBoundingClientRect(), f = frame.getBoundingClientRect();
    window.dispatchEvent(new CustomEvent("lr-select", { detail: { quote, x: f.left + r.left + r.width / 2, y: f.top + r.top, frame } }));
  }, 10);
  doc.addEventListener("mouseup", up);
  return () => doc.removeEventListener("mouseup", up);
}

export function CommentPill({ picked, onOpen }: { picked: Picked; onOpen: () => void }) {
  const style = picked.touch ? undefined : { left: Math.max(60, Math.min(window.innerWidth - 60, picked.x)), top: Math.max(8, picked.y - 44) };
  return (
    <button className={`lr-selpill ${picked.touch ? "touch" : ""}`} style={style} onMouseDown={(e) => e.preventDefault()} onClick={onOpen}>
      💬 Comment on this
    </button>
  );
}

export function Composer({ quote, recording, onSave, onMic, onClose, frame, time }: { quote: string; recording: boolean; frame?: string | null; time?: number; onSave: (text: string) => void; onMic: () => void; onClose: () => void }) {
  const [text, setText] = useState("");
  return (
    <div className="lr-ovl" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="lr-tray lr-reason lr-composer" role="dialog" aria-label={quote ? "Comment on the selected words" : "Comment on this item"}>
        <div className="top"><b>Comment</b><button onClick={onClose} aria-label="Close">✕</button></div>
        {time != null ? <div className="lr-fpin"><div className="lr-ftime">▸ {clock(time)}</div>{frame ? <img className="lr-fthumb big" src={`data:image/jpeg;base64,${frame}`} alt="The frame you're commenting on" /> : <div className="hint">The picture couldn't be saved, so this comment goes with the time only.</div>}</div> : quote ? <blockquote>“{quote.length > 240 ? `${quote.slice(0, 240)}…` : quote}”</blockquote> : <div className="hint">A note on the whole item. To comment on certain words, select them first.</div>}
        {/* ⌘+Enter (Ctrl+Enter) saves, Esc closes; a plain Enter is a new line */}
        <textarea autoFocus value={text} onChange={(e) => setText(e.target.value)} placeholder="What should change here, or why it works" aria-label="Your comment"
          onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); if (text.trim()) onSave(text); } else if (e.key === "Escape") onClose(); }} />
        <div style={{ display: "flex", gap: 8 }}>
          <button className={`mic ${recording ? "rec" : ""}`} onClick={onMic} aria-label={recording ? "Stop and save the voice note" : "Say it instead"}><MicIcon /></button>
          <button className="cancel" onClick={onClose}>Cancel</button>
          <button className="send okb" disabled={!text.trim()} onClick={() => onSave(text)} title="Save (⌘ Enter)">Save comment</button>
        </div>
      </div>
    </div>
  );
}

// SH-1 / SH-2 — who wrote each comment, and whether you may change it. A signed-in room fills
// this from the review (people on the team, your email, whether you own it); with nothing
// provided (a share link, an older server) names stay hidden and every comment stays editable.
export type Person = { name: string; initials: string; color: string | null };
export type Who = { people: Record<string, Person>; me: string | null; owner: boolean };
export const WhoContext = createContext<Who | null>(null);
const PALETTE = ["#2563eb", "#db2777", "#059669", "#d97706", "#7c3aed", "#0891b2"];
export function writerOf(who: Who, by: string | null): Person {
  const key = String(by || "").toLowerCase();
  const known = who.people[key];
  if (known) return { ...known, initials: known.initials || initialsOf(known.name), color: known.color || colorFor(key) };
  if (!key.includes("@")) return { name: key ? key[0].toUpperCase() + key.slice(1) : "Someone", initials: "?", color: "#64748b" };
  const name = key.split("@")[0].split(/[._-]+/).filter(Boolean).map((w) => w[0].toUpperCase() + w.slice(1)).join(" ");
  return { name, initials: initialsOf(name), color: colorFor(key) };
}
const initialsOf = (name: string) => name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join("");
const colorFor = (key: string) => PALETTE[[...key].reduce((a, c) => a + c.charCodeAt(0), 0) % PALETTE.length];
export const canChange = (who: Who | null, n: Note) => !who || who.owner || (!!who.me && String(n.by || "").toLowerCase() === who.me);

const clock = (sec: number) => `${Math.floor(sec / 60)}:${String(Math.floor(sec % 60)).padStart(2, "0")}`;
const when = (at: string) => { const d = new Date(at); return isNaN(+d) ? "" : d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); };
export type NoteEdits = { onEdit: (n: Note, text: string) => Promise<boolean>; onDelete: (n: Note) => Promise<boolean> };

export function ItemNotes({ notes, onPlay, edits, onOpen, reviewId }: { notes: Note[]; onPlay: (n: Note) => void; edits?: NoteEdits; onOpen?: (n: Note) => void; reviewId?: string }) {
  if (!notes.length) return null;
  return (
    <div className="lr-notes">
      <div className="h">Notes · {notes.length}</div>
      {notes.map((n) => <OneNote key={n.id} reviewId={reviewId} n={n} onPlay={onPlay} edits={edits} onOpen={n.anchor ? onOpen : undefined} />)}
    </div>
  );
}

// a note, with Edit (change its words in place) and Delete (a second tap confirms; no pop-up)
// onOpen: tapping the note (anywhere but its buttons) jumps to the place on the page it's about
// the still the reviewer was looking at when they left a frame comment
function FrameThumb({ reviewId, noteId }: { reviewId: string; noteId: string }) {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => { let alive = true; noteFrame(reviewId, noteId).then((u) => alive && setSrc(u), () => {}); return () => { alive = false; }; }, [reviewId, noteId]);
  return src ? <img className="lr-fthumb" src={src} alt="The frame this comment is about" /> : null;
}

function OneNote({ n, onPlay, edits, onOpen, reviewId }: { n: Note; onPlay: (n: Note) => void; edits?: NoteEdits; onOpen?: (n: Note) => void; reviewId?: string }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(n.text || "");
  const [sure, setSure] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (!sure) return; const t = setTimeout(() => setSure(false), 4000); return () => clearTimeout(t); }, [sure]);
  const save = async () => { setBusy(true); if (await edits!.onEdit(n, text)) setEditing(false); setBusy(false); };
  const who = useContext(WhoContext);
  const writer = who ? writerOf(who, n.by) : null;
  const mayEdit = edits && canChange(who, n);
  return (
    <div className={onOpen && !editing ? "n go" : "n"} title={onOpen && !editing ? "Show this on the page" : undefined}
      onClick={(e) => { if (onOpen && !editing && !(e.target as HTMLElement).closest("button, textarea")) onOpen(n); }}>
      {writer && <div className="lr-by"><span className="av" style={{ background: writer.color || undefined }}>{writer.initials}</span><b>{writer.name}</b><span className="at">{when(n.at)}</span></div>}
      {n.kind === "frame" && <div className="lr-ftime">▸ {clock(Number((n.anchor || "").replace(/^t=/, "")) || 0)}{n.round ? ` · round ${n.round}` : ""}</div>}
      {n.kind === "frame" && reviewId && <FrameThumb reviewId={reviewId} noteId={n.id} />}
      {n.quote && <mark style={writer?.color ? { background: `${writer.color}2e`, boxShadow: `inset 2px 0 0 ${writer.color}` } : undefined}>“{n.quote.length > 140 ? `${n.quote.slice(0, 140)}…` : n.quote}”</mark>}
      {editing ? (
        <>
          <textarea className="lr-edit" value={text} autoFocus rows={3} onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) save(); if (e.key === "Escape") setEditing(false); }} />
          <div className="lr-nacts">
            <button className="sv" disabled={busy || (!text.trim() && !n.file)} onClick={save}>Save</button>
            <button onClick={() => { setEditing(false); setText(n.text || ""); }}>Cancel</button>
          </div>
        </>
      ) : (
        <>
          {n.text && <div className="t">{n.text}</div>}
          {n.transcript && !n.text && <div className="t">{n.transcript}</div>}
          {n.file && <button className="lr-listen" onClick={() => onPlay(n)}>▶ Voice note</button>}
          {mayEdit && edits && (
            <div className="lr-nacts">
              <button onClick={() => { setText(n.text || ""); setEditing(true); }}>{n.text ? "Edit" : "Add words"}</button>
              <button className={sure ? "del sure" : "del"} disabled={busy}
                onClick={async () => { if (!sure) return setSure(true); setBusy(true); await edits.onDelete(n); setBusy(false); }}>{sure ? "Tap again to delete" : "Delete"}</button>
              {n.editedAt && <span className="ed">edited</span>}
            </div>
          )}
        </>
      )}
    </div>
  );
}
